import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { useUser } from '../components/UserContext.tsx';
import { canViewReviewQueue } from './schoolClaims.ts';
import { decideReviewExcerpt, fetchReviewQueue, type ReviewExcerpt } from './schoolApi.ts';
import SchoolDeskPage from './SchoolDeskPage.tsx';

export default function SchoolReviewPage() {
  const { activeUser } = useUser();
  const [items, setItems] = useState<ReviewExcerpt[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  const reload = useCallback(async () => {
    const data = await fetchReviewQueue();
    setItems(data.items || []);
  }, []);

  useEffect(() => {
    if (!canViewReviewQueue(activeUser)) return;
    let cancelled = false;
    reload().catch((cause) => {
      if (!cancelled) setError(cause instanceof Error ? cause.message : 'Could not load queue');
    });
    return () => {
      cancelled = true;
    };
  }, [activeUser, reload]);

  if (!canViewReviewQueue(activeUser)) {
    return (
      <SchoolDeskPage title="Blinded review">
        <Alert variant="secondary">This queue is only for the contracted safety desk.</Alert>
      </SchoolDeskPage>
    );
  }

  const decide = async (excerptToken: string, decision: string) => {
    setBusy(excerptToken);
    setError(null);
    try {
      await decideReviewExcerpt(excerptToken, decision);
      await reload();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Could not save that decision');
    } finally {
      setBusy(null);
    }
  };

  return (
    <SchoolDeskPage title="Blinded review">
      <p>
        Excerpts have no school, class, or account identifiers. Return a category, not a name.
        Escalating to red is only for words that already meet the red threshold.
      </p>
      {error && <Alert variant="danger">{error}</Alert>}
      {items == null && !error && <Spinner animation="border" />}
      {items && items.length === 0 && <p>Queue is empty.</p>}
      {items && items.map((item) => (
        <Card key={item.excerptToken} className="mb-3">
          <Card.Body>
            <div>{item.text}</div>
            <div className="text-muted mb-2">{item.relativeTime || 'recent'}</div>
            <Button
              className="me-2"
              size="sm"
              disabled={busy === item.excerptToken}
              onClick={() => decide(item.excerptToken, 'possible-exclusion')}
            >
              Possible exclusion
            </Button>
            <Button
              className="me-2"
              size="sm"
              variant="outline-secondary"
              disabled={busy === item.excerptToken}
              onClick={() => decide(item.excerptToken, 'not-a-case')}
            >
              Not a case
            </Button>
            <Button
              size="sm"
              variant="outline-danger"
              disabled={busy === item.excerptToken}
              onClick={() => decide(item.excerptToken, 'escalate-red')}
            >
              Escalate to red
            </Button>
          </Card.Body>
        </Card>
      ))}
    </SchoolDeskPage>
  );
}
